import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import SetSaleBillBottom from "../saleBill/SetSaleBillBottom";
import SetSaleBillMiddle from "../saleBill/SetSaleBillMiddle";
import SetSaleBillTop from "../saleBill/SetSaleBillTop";
import BillTotals from "../saleBill/BillTotals";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useBill } from "hooks/bill";
import useFullScreenLayout from "hooks/useFullScreenLayout";

export default function SetCreditDebitNote() {
  useFullScreenLayout();

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id || location.state?.id || null;
  const isEditMode = !!id;

  const [calculatedValues, setCalculatedValues] = useState({
    totalAmountAfterAllDiscounts: 0,
    freightAfterTax: 0,
    tcsAmount: 0,
  });

  const {
    billHeader,
    tableData,
    totalGross,
    totalTax,
    totalQtyPcs,
    selectBoxes,
    isIGST,
    beforeTaxDiscounts,
    afterTaxDiscounts,
    agentCommission,
    taxBreakdown,
    traderBillType,
    setTraderBillType,
    createSaleBill,
    isCreatingSaleBill,
    updateSaleBill,
    isUpdatingSaleBill,
    resetStore,
    handleVendorChange,
    handleCustomerChange,
    handleBillTypeChange,
    handleInputChange,
    updateBillHeader,
    updateSelectBox,
  } = useBill();

  const prefillApplied = useRef(false);

  useEffect(() => {
    if (prefillApplied.current) return;
    if (searchParams.get("fromMR") !== "true") return;
    prefillApplied.current = true;

    const noteType = searchParams.get("noteType");
    const vendorId = searchParams.get("vendorId");
    const vendorName = searchParams.get("vendorName");
    const customerId = searchParams.get("customerId");
    const customerName = searchParams.get("customerName");
    const date = searchParams.get("date");
    const remark = searchParams.get("remark");

    if (noteType) {
      const label = noteType === "VCN" ? "Credit Note" : "Debit Note";
      handleBillTypeChange({ value: noteType, label });
      setTraderBillType(noteType === "VCN" ? "S" : "P");
    }
    if (vendorId) {
      handleVendorChange({ ps_Id: vendorId, name: vendorName || vendorId });
    }
    if (customerId) {
      handleCustomerChange({ ps_Id: customerId, name: customerName || customerId });
    }
    if (remark) {
      handleInputChange("remark", remark);
    }

    updateSelectBox("cndnType", {
      value: "__direct__",
      label: "Direct (Without Item)",
      name: "Direct(Without Item)",
    });
    updateBillHeader({
      cndntype: "Direct(Without Item)",
      billdefault: 0,
      ...(date ? { salebilldate: date } : {}),
    });
  }, [searchParams, handleBillTypeChange, handleVendorChange, handleCustomerChange, handleInputChange, updateBillHeader, updateSelectBox, setTraderBillType]);

  useEffect(() => {
    return () => {
      resetStore();
    };
  }, [resetStore]);

  const handleSubmitForm = async () => {
    if (!selectBoxes.billType || !["VCN", "VDN"].includes(selectBoxes.billType.value)) {
      toast.error("Please select Credit Note or Debit Note type");
      return;
    }

    const isDirectWithoutItem = billHeader.cndntype === "Direct(Without Item)";

    if (!isDirectWithoutItem && !billHeader.cndntype) {
      toast.error("Please select a CN/DN Type");
      return;
    }

    const filteredTableData = isDirectWithoutItem
      ? []
      : tableData.filter(item => item.selected);

    if (!isDirectWithoutItem && filteredTableData.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    if (isDirectWithoutItem && totalGross <= 0) {
      toast.error("Please enter Gross Amount");
      return;
    }

    const structuredData = {
      billHeader: {
        ...billHeader,
        billdefault: isDirectWithoutItem ? 0 : 1,
      },
      tableData: filteredTableData,
      selectBoxes,
      docType: "CNDN",
      traderbilltyp: traderBillType,
      settlementMode: "CREDIT",
      calculations: {
        totalGross,
        totalTax,
        totalQtyPcs,
        isIGST,
        ...taxBreakdown,
      },
      discounts: beforeTaxDiscounts,
      afterTaxDiscounts,
      agentCommission,
      additionalAmounts: {
        tcsAmount: calculatedValues.tcsAmount || 0,
        tcsPercentage: selectBoxes.vendor?.tcsperc || 0,
        freightAfterTax: calculatedValues.freightAfterTax || 0,
        totalAmountAfterAllDiscounts: calculatedValues.totalAmountAfterAllDiscounts || 0,
      }
    };

    try {
      if (isEditMode) {
        await updateSaleBill({ id, data: structuredData });
      } else {
        await createSaleBill(structuredData);
      }
      navigate('/cndn');
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} credit/debit note:`, error);
    }
  };

  const handleCalculatedValues = useCallback((values) => {
    setCalculatedValues(values);
  }, []);

  return (
    <form>
      <div className="space-y-8 px-4 sm:px-6 lg:px-8">
        <SetSaleBillTop id={id} docType="CNDN" />
        <SetSaleBillMiddle docType="CNDN" />
        <BillTotals docType="CNDN" />
        <SetSaleBillBottom
          totalGross={totalGross}
          totalTax={totalTax}
          isIGST={isIGST}
          docType="CNDN"
          handleSubmitForm={handleSubmitForm}
          onCalculatedValues={handleCalculatedValues}
          isSubmitting={isCreatingSaleBill || isUpdatingSaleBill}
          isEditMode={isEditMode}
        />
      </div>
    </form>
  );
}
